import { useState, useRef } from 'react';
import { useAppStore } from "../../hooks/useAppStore";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Box,
  Tooltip,
  Badge,
  TextField,
  Stack
} from '@mui/material';

//import AddIcon from '@mui/icons-material/Add';
import { AddCircleRounded as AddCircleRoundedIcon, Close as CloseIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import wordOptions from '../../locales/en-word-options.json';

export default function WordSelectionAccordion() {
  const {
    selectedWords, setSelectedWords,
    selectedLayout, setSelectedLayout,
    selectedShape, setSelectedShape,
    resetSetup,
  } = useAppStore();

  const [expandedCategory, setExpandedCategory] = useState(null);
  const [newWord, setNewWord] = useState({});
  const [addWordError, setAddWordError] = useState({});  
  const [wordGroups, setWordGroups] = useState({});
  const [showAddGroupInput, setShowAddGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [addGroupError, setAddGroupError] = useState('');
  const [showAddWordInput, setShowAddWordInput] = useState({});

  const mergedGroups = [
    ...Object.entries(wordOptions),
    ...Object.entries(wordGroups),
  ];
  

  const handleAddWord = (category, words) => {
    const wordToAdd = newWord[category]?.trim();
    if (!wordToAdd) return;
  
    // Check if the word is already selected in any category
    const existingCategory = Object.entries(selectedWords).find(
      ([cat, wordArray]) =>
        wordArray.includes(wordToAdd) && cat !== category
    )?.[0];
  
    if (existingCategory) {
      setAddWordError((prev) => ({
        ...prev,
        [category]: `"${wordToAdd}" is already selected in ${existingCategory}`,
      }));
      return;
    }
  
    setAddWordError((prev) => ({ ...prev, [category]: '' }));
  
    // Optionally: check if it already exists in the display list
    const existsInThisCategory = words.some(
      ({ word }) => word.toLowerCase() === wordToAdd.toLowerCase()
    );
  
    if (!existsInThisCategory) {
      words.push({ word: wordToAdd });
    }
  
    setSelectedWords((prev) => {
      const updated = { ...prev };
      // Remove the word from any previous category
      for (const cat in updated) {
        updated[cat] = updated[cat].filter((w) => w !== wordToAdd);
      }
      // Add to the current category
      if (!updated[category]) updated[category] = [];
      updated[category].push(wordToAdd);
      return updated;
    });
  
    setNewWord((prev) => ({ ...prev, [category]: '' }));
  };

  const handleCreateGroup = () => {
    const trimmedName = newGroupName.trim();
    if (!trimmedName) {
      setAddGroupError('Group name cannot be empty');
      return;
    }
    if (wordGroups[trimmedName]) {
      setAddGroupError('Group name already exists');
      return;
    }
  
    setWordGroups((prev) => ({
      ...prev,
      [trimmedName]: [],
    }));
    setExpandedCategory(trimmedName);
    setNewGroupName('');
    setAddGroupError('');
    setShowAddGroupInput(false);
  };


  const toggleWord = (category, word) => {
    const newSelectedWords = { ...selectedWords };

    // Ensure category exists
    if (!newSelectedWords[category]) {
      newSelectedWords[category] = [];
    }
  
    const alreadySelected = newSelectedWords[category].includes(word);
  
    if (alreadySelected) {
      // Remove word from category
      newSelectedWords[category] = newSelectedWords[category].filter(w => w !== word);
    } else {
      // Remove word from other categories if it's globally unique
      for (const cat in newSelectedWords) {
        newSelectedWords[cat] = newSelectedWords[cat].filter(w => w !== word);
      }
  
      // Add word to this category
      newSelectedWords[category].push(word);
    }
  
    setSelectedWords(newSelectedWords);
  };

  return (
    <Box>
      <Typography
      variant="h6"
      sx={{
        fontWeight: 'normal',
      }}
    >
      -You can always add more later
      -Pick words that your pet already knows first
      -Pick words that are useful to your pet
      -If you are just starting, people usually start from one or two words from Common First Words category
    </Typography>
      {mergedGroups.map(([category, words]) => {
        const selectedWordsForCategory = selectedWords?.[category] || [];
        const selectedCount = selectedWordsForCategory.length;

        return (
          <Box
            key={category}
            sx={{
              borderRadius: '50px',
              overflow: 'hidden',
              backgroundColor: 'var(--tekhelet)',
              mb: 2,
            }}
          >


            <Accordion
              expanded={expandedCategory === category}
              onChange={() =>
                setExpandedCategory(expandedCategory === category ? null : category)
              }
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                borderRadius: '50px',
                '&::before': { display: 'none' },
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '&.MuiAccordionSummary-root': {
                    height: '40px',
                    paddingX: '40px',
                    paddingTop: '15px',
                  },
                  '& .MuiAccordionSummary-content': {
                    marginRight: '10px',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between', // distribute title and badge+icon
                    width: '100%',
                  },
                  '&.Mui-expanded': {
                    minHeight: '64px',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: expandedCategory === category ? 'var(--tekhelet)' : 'white',
                    transition: 'color 0.3s',
                  }}
                >
                  {category}
                </Typography>

                {selectedCount > 0 && (
                  <Badge
                    badgeContent={selectedCount}
                    color="warning"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: 'var(--golden-yellow)',
                        color: '#000',
                        borderRadius: '50%',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        minWidth: 24,
                        height: 24,
                        marginRight: '8px',
                      },
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', pl: '20px', pr: '20px' }}>
                              {words.map(({ word }) => {
                                const isSelected = selectedWords?.[category]?.includes(word);
                                const isSelectedAnywhere = Object.values(selectedWords || {}).some((words) => words.includes(word));
                                const isDisabled = isSelectedAnywhere && !isSelected;
                                const selectedCategory = Object.entries(selectedWords || {}).find(
                                  ([, words]) => words.includes(word)
                                )?.[0];
                                
                                const button = (
                                  <Button
                                    key={word}
                                    onClick={() => toggleWord(category, word)}
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    disabled={isDisabled}
                                    sx={{
                                      borderRadius: '999px',
                                      textTransform: 'none',
                                      px: 2,
                                      py: 1,
                                      m: '4px',
                                      backgroundColor: isSelected
                                        ? 'var(--golden-yellow)'
                                        : 'transparent',
                                      borderColor: isSelected
                                        ? 'var(--golden-yellow)'
                                        : '#fff',
                                      color: isSelected
                                        ? '#000'
                                        : isDisabled
                                        ? '#ccc'
                                        : '#fff',
                                      opacity: isDisabled ? 0.4 : 1,
                                      '&:hover': {
                                        backgroundColor: isSelected
                                          ? 'var(--golden-yellow)'
                                          : isDisabled
                                          ? 'transparent'
                                          : 'rgba(255,255,255,0.1)',
                                      },
                                    }}
                                  >
                                    {word}
                                  </Button>
                                );

                                return isDisabled ? (
                                  <Tooltip
                                    key={word}
                                    title={
                                      <Typography sx={{ fontWeight: 'normal' }}>
                                        Already added under <strong>{selectedCategory}</strong>
                                      </Typography>
                                    }
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          borderRadius: '12px',
                                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                          color: '#fff',
                                          fontSize: '0.875rem',
                                          padding: '8px 12px',
                                        },
                                      },
                                    }}
                                  >
                                    <span>{button}</span>
                                  </Tooltip>
                                ) : (
                                  button
                                );
                              })}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!showAddWordInput[category] ? (
                      <Typography
                      variant="body2"
                      onClick={() =>
                        setShowAddWordInput((prev) => ({ ...prev, [category]: true }))
                      }
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        alignSelf: 'flex-start',
                        padding: '10px 20px;',
                        mt: 1,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                      >
                      <AddCircleRoundedIcon /> Add new word
                      </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            value={newWord[category] || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNewWord((prev) => ({ ...prev, [category]: value }));

                              const existingCategory = Object.entries(selectedWords).find(
                                ([w]) => w.toLowerCase() === value.toLowerCase()
                              )?.[1];

                              setAddWordError((prev) => ({
                                ...prev,
                                [category]: existingCategory
                                  ? `"${value}" is already selected in ${existingCategory}`
                                  : '',
                              }));
                            }}
                            placeholder="Add word"
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: '500px' }}
                            inputProps={{ maxLength: 50 }}
                            error={!!addWordError[category]}
                            helperText={addWordError[category]}
                          />
                          <Button
                            onClick={() => handleAddWord(category, words)}
                            disabled={
                              !newWord[category]?.trim() || Boolean(addWordError[category])
                            }
                            sx={{
                              borderRadius: '999px',
                              textTransform: 'none',
                              fontWeight: 'bold',
                              px: 3,
                              py: 1,
                              backgroundColor: 'var(--orange-peel)',
                              color: 'var(--tekhelet)',
                              minWidth: 0,
                              '&:hover': {
                                backgroundColor: '#ffd966',
                              },
                            }}
                          >
                            <AddCircleRoundedIcon sx={{ mr: 1 }} /> Add
                          </Button>

                          <Button
                            onClick={() =>
                              setShowAddWordInput((prev) => ({ ...prev, [category]: false }))
                            }
                            sx={{
                              borderRadius: '999px',
                              textTransform: 'none',
                              fontWeight: 'bold',
                              px: 2,
                              py: 1,
                              backgroundColor: 'var(--bright-yellow)',
                              color: 'var(--tekhelet)',
                              minWidth: 0,
                              '&:hover': {
                                backgroundColor: '#ffd966',
                              },
                            }}
                          >
                            <CloseIcon />
                          </Button>
                        </Box>
                    )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      })}
      {/* Add group section */}
      <Box mt={4} display="flex" justifyContent="flex-start">
        {showAddGroupInput ? (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              size="small"
              label="New group name"
              value={newGroupName}
              onChange={(e) => {
                setNewGroupName(e.target.value.slice(0, 50)); // Limit to 50 characters
                setAddGroupError('');
              }}
              error={!!addGroupError}
              helperText={addGroupError}
              sx={{ flexGrow: 1, maxWidth: 500 }}
              inputProps={{ maxLength: 50 }}
            />
            <Button
              variant="contained"
              onClick={handleCreateGroup}
              disabled={!newGroupName.trim()}
              startIcon={<AddCircleRoundedIcon />}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 'bold',
                px: 3,
                py: 1.5,
                backgroundColor: 'var(--orange-peel)',
                color: 'var(--tekhelet)',
                '&:hover': {
                  backgroundColor: '#ffd966',
                },
              }}
            >
              Add
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowAddGroupInput(false);
                setNewGroupName('');
                setAddGroupError('');
              }}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 'bold',
                px: 2,
                py: 1.5,
                backgroundColor: 'var(--orange-peel)',
                color: 'var(--tekhelet)',
                '&:hover': {
                  backgroundColor: '#ffd966',
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddCircleRoundedIcon />}
            onClick={() => setShowAddGroupInput(true)}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              backgroundColor: 'var(--bright-yellow)',
              color: 'var(--tekhelet)',
              '&:hover': {
                backgroundColor: '#ffd966',
              },
            }}
          >
            Add Group
          </Button>
        )}
      </Box>
    </Box>
  );
}